import { describeMutationError } from "@api/mutationErrors";
import { BoxOverlay } from "@perception/BoxOverlay";
import { type SourceSize, toDisplay } from "@perception/fixtureBoxes";
import { StillFrame } from "@perception/StillFrame";
import {
  type CameraArea,
  type CameraAreasChange,
  MAX_CAMERA_AREAS,
  type RoomCameraAreas,
} from "@rooms/roomAreasClient";
import type { FixtureBox } from "@rooms/roomsClient";
import {
  Button,
  Dialog,
  FieldError,
  SegmentedControl,
} from "@scribenote/brian-design-system";
import { useState } from "react";

const AREA_LABELS = ["Receptionist desk", "Client waiting area"] as const;

export interface AreaPicture {
  source: "baseline" | "still";
  url: string;
  reference: string | null;
  expectedBaselineAt?: string;
}
export interface DoorReference {
  width: number;
  height: number;
  active: boolean;
  doors: { key: string; label: string; box: FixtureBox }[];
}
export const RoomAreasEditor = ({
  roomName,
  pictures,
  saved,
  onSave,
  onClose,
  onRefresh,
  doorReference,
  doorReferenceNotice,
}: {
  roomName: string;
  pictures: AreaPicture[];
  saved: RoomCameraAreas | null;
  onSave: (change: CameraAreasChange) => Promise<unknown>;
  onClose: () => void;
  onRefresh?: () => Promise<unknown>;
  doorReference?: DoorReference | null;
  doorReferenceNotice?: string;
}) => {
  const [initialSaved] = useState(saved);
  const [initialBaseline] = useState(
    pictures.find((p) => p.source === "baseline"),
  );
  const [source, setSource] = useState(pictures[0]?.source ?? "still");
  const picture =
    source === "baseline"
      ? initialBaseline
      : pictures.find((p) => p.source === source);
  const [loaded, setLoaded] = useState<{
    url: string;
    size: SourceSize;
  } | null>(null);
  const [imageFailed, setImageFailed] = useState<string | null>(null);
  const size = loaded?.url === picture?.url ? loaded?.size : undefined;
  const doorsFit =
    !!size &&
    doorReference?.active === true &&
    doorReference.width === size.width &&
    doorReference.height === size.height;
  const referenceDoors = doorsFit ? doorReference.doors : [];
  const [draft, setDraft] = useState<{
    size: SourceSize;
    areas: CameraArea[];
  } | null>(null);
  const baseSize = draft?.size ?? initialSaved;
  const mismatch =
    !!size &&
    !!baseSize &&
    (size.width !== baseSize.width || size.height !== baseSize.height);
  const inactive = initialSaved?.active === false && draft === null;
  const blocked = mismatch || inactive;
  const areas = blocked ? [] : (draft?.areas ?? initialSaved?.areas ?? []);
  const [drawing, setDrawing] = useState(
    (initialSaved?.areas.length ?? 0) === 0,
  );
  const [pending, setPending] = useState<FixtureBox | null>(null);
  const [redraw, setRedraw] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const busy = saving || refreshing;
  const unfinished = pending !== null || renaming !== null || redraw !== null;
  const controlsDisabled = busy || unfinished;
  const update = (next: CameraArea[]) => {
    if (size) setDraft({ size, areas: next });
  };
  const forget = () => {
    setDrawing(false);
    setPending(null);
    setRedraw(null);
    setRenaming(null);
    setLabel("");
  };
  const save = async () => {
    if (!picture || !size || blocked) return;
    setSaving(true);
    setProblem(null);
    try {
      await onSave({
        drawn_on: source,
        expected_baseline_at:
          source === "baseline" ? picture.expectedBaselineAt : undefined,
        still_reference: picture.reference,
        areas,
      });
      onClose();
    } catch (error) {
      setProblem(describeMutationError(error, "save the areas"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      title={`Areas of ${roomName}`}
      size="takeover"
      dismissable={!busy}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="accent"
            busy={saving}
            disabled={
              controlsDisabled ||
              !size ||
              !picture ||
              imageFailed === picture.url ||
              blocked
            }
            onClick={() => void save()}
          >
            Save areas
          </Button>
        </div>
      }
    >
      <div
        data-dd-privacy="hidden"
        className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]"
      >
        <div className="flex min-w-0 flex-col gap-3">
          {pictures.length > 1 && (
            <SegmentedControl<AreaPicture["source"]>
              aria-label="Picture to draw on"
              value={source}
              options={[
                {
                  value: "baseline",
                  label: "Empty-room baseline",
                  disabled: controlsDisabled,
                },
                {
                  value: "still",
                  label: "Live picture",
                  disabled: controlsDisabled,
                },
              ]}
              onValueChange={(value) => {
                setSource(value);
                forget();
              }}
            />
          )}
          {!picture && (
            <p className="text-sm text-ink-body">
              No picture from this room’s camera yet. Close this editor and try
              again once a still is available.
            </p>
          )}
          {picture && (
            <StillFrame
              url={picture.url}
              onLoad={(natural) => {
                setLoaded({ url: picture.url, size: natural });
                setImageFailed(null);
              }}
              onError={() => setImageFailed(picture.url)}
            >
              {size && !blocked && (
                <BoxOverlay
                  size={size}
                  tone="area"
                  label="Reception area drawing"
                  boxes={areas.map((a) => ({
                    key: a.uuid,
                    box: a.box,
                    label: a.label,
                  }))}
                  draft={pending}
                  disabled={
                    busy ||
                    (!drawing && !redraw) ||
                    pending !== null ||
                    renaming !== null ||
                    (areas.length >= MAX_CAMERA_AREAS && !redraw)
                  }
                  onDraw={(box) => {
                    if (redraw) {
                      update(
                        areas.map((a) =>
                          a.uuid === redraw ? { ...a, box } : a,
                        ),
                      );
                      setRedraw(null);
                    } else {
                      setPending(box);
                      setLabel("");
                    }
                  }}
                />
              )}
              {size && referenceDoors.length > 0 && (
                <>
                  <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    role="group"
                    aria-label="Saved doors, read only"
                    className="pointer-events-none absolute inset-0 h-full w-full"
                  >
                    {referenceDoors.map((door) => (
                      <rect
                        key={door.key}
                        {...toDisplay(door.box, size)}
                        vectorEffect="non-scaling-stroke"
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        fillOpacity={0.12}
                        className="fill-fill-recorded stroke-ink-recorded"
                      />
                    ))}
                  </svg>
                  {referenceDoors.map((door) => (
                    <span
                      key={door.key}
                      className="pointer-events-none absolute rounded-paper border border-ink-recorded bg-card px-1 text-fine-print text-ink-recorded"
                      style={{
                        left: `${(door.box[0] / size.width) * 100}%`,
                        top: `${(door.box[1] / size.height) * 100}%`,
                      }}
                    >
                      {door.label}
                    </span>
                  ))}
                </>
              )}
            </StillFrame>
          )}
          {picture && !size && !imageFailed && (
            <p className="text-sm text-annotation">Loading picture…</p>
          )}
          {imageFailed === picture?.url && (
            <FieldError>
              Couldn’t load the camera picture. Close and reopen the editor to
              try again.
            </FieldError>
          )}
          {source === "still" && onRefresh && (
            <Button
              variant="ghost"
              size="sm"
              disabled={controlsDisabled}
              onClick={() => {
                setRefreshing(true);
                setProblem(null);
                void onRefresh()
                  .catch((error: unknown) =>
                    setProblem(
                      describeMutationError(error, "load a newer picture"),
                    ),
                  )
                  .finally(() => setRefreshing(false));
              }}
            >
              Use a newer picture
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span className="text-ink-brian">Blue · editable areas</span>
            <span className="text-ink-recorded">
              Green dashed · saved doors (read only)
            </span>
          </div>
          {doorReferenceNotice && (
            <p className="text-sm text-annotation">{doorReferenceNotice}</p>
          )}
          {doorReference &&
            doorReference.doors.length > 0 &&
            size &&
            !doorsFit && (
              <p className="text-sm text-ink-attention">
                Saved doors are hidden because their camera or picture size does
                not match. Open “Draw doors on its camera” to update them.
              </p>
            )}
          <p className="text-sm text-ink-body">
            Draw each area and choose its label. Add more areas before saving.
          </p>
          {blocked && (
            <div className="flex flex-col gap-2">
              <FieldError>
                {mismatch
                  ? "The saved areas use a different picture size. Draw them again on this picture."
                  : "The camera has changed since these areas were saved. Draw them again."}
              </FieldError>
              <Button
                variant="outline"
                disabled={busy || !size}
                onClick={() => {
                  update([]);
                  forget();
                }}
              >
                Start again on this picture
              </Button>
            </div>
          )}
          {pending || renaming ? (
            <div className="flex flex-col gap-2">
              <div
                role="group"
                aria-label="Area label"
                className="flex flex-col gap-2"
              >
                <p className="text-sm text-ink-body">
                  Choose a label for this area.
                </p>
                {AREA_LABELS.map((choice) => (
                  <Button
                    key={choice}
                    size="sm"
                    variant={label === choice ? "accent" : "outline"}
                    aria-pressed={label === choice}
                    disabled={busy}
                    onClick={() => setLabel(choice)}
                  >
                    {choice}
                  </Button>
                ))}
              </div>
              <Button
                size="sm"
                variant="accent"
                disabled={
                  !AREA_LABELS.some((choice) => choice === label) || busy
                }
                onClick={() => {
                  if (renaming)
                    update(
                      areas.map((a) =>
                        a.uuid === renaming ? { ...a, label: label.trim() } : a,
                      ),
                    );
                  else if (pending)
                    update([
                      ...areas,
                      {
                        uuid: crypto.randomUUID(),
                        label: label.trim(),
                        box: pending,
                      },
                    ]);
                  forget();
                }}
              >
                {renaming ? "Update label" : "Add area"}
              </Button>
              <Button size="sm" variant="ghost" onClick={forget}>
                Discard {renaming ? "label change" : "rectangle"}
              </Button>
            </div>
          ) : (
            <>
              {!blocked &&
                !redraw &&
                (drawing ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-ink-body" role="status">
                      Drag a rectangle on the picture, then choose its label.
                    </p>
                    <Button size="sm" variant="ghost" onClick={forget}>
                      Cancel adding
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || !size || areas.length >= MAX_CAMERA_AREAS}
                    onClick={() => {
                      setProblem(null);
                      setDrawing(true);
                    }}
                  >
                    {areas.length > 0 ? "Add another area" : "Add area"}
                  </Button>
                ))}
              {redraw && (
                <div>
                  <p className="text-sm text-ink-body">
                    Drag a new rectangle for{" "}
                    {areas.find((a) => a.uuid === redraw)?.label}.
                  </p>
                  <Button variant="ghost" size="sm" onClick={forget}>
                    Cancel redraw
                  </Button>
                </div>
              )}
              {!blocked && areas.length === 0 && (
                <p className="text-sm text-annotation">No areas drawn yet.</p>
              )}
              {areas.length >= MAX_CAMERA_AREAS && (
                <p className="text-sm text-annotation">
                  A room holds at most {MAX_CAMERA_AREAS} areas.
                </p>
              )}
              <ul className="flex flex-col">
                {areas.map((area) => (
                  <li
                    key={area.uuid}
                    className="flex flex-col gap-1 border-b border-rule py-2"
                  >
                    <span className="break-words text-sm text-ink">
                      {area.label}
                    </span>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setRenaming(area.uuid);
                          setRedraw(null);
                          setLabel(
                            AREA_LABELS.some((choice) => choice === area.label)
                              ? area.label
                              : "",
                          );
                        }}
                      >
                        Edit label
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setDrawing(false);
                          setRedraw(area.uuid);
                        }}
                      >
                        Redraw
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          update(areas.filter((a) => a.uuid !== area.uuid));
                          if (redraw === area.uuid) setRedraw(null);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          {problem && <FieldError>{problem}</FieldError>}
        </div>
      </div>
    </Dialog>
  );
};
